import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { 
  Home, 
  LayoutDashboard, 
  Wrench, 
  MessageSquare, 
  BookOpen,
  CreditCard,
  Settings,
  LogOut,
  Sun,
  Moon,
  HelpCircle,
  Sparkles,
  FileSpreadsheet,
  Bot,
  FileText,
  BookMarked,
} from "lucide-react";
import { useTheme } from "next-themes";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onShowHelp: () => void;
}

export function CommandPalette({ open, onOpenChange, onShowHelp }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const { signOut, user } = useAuth();
  const [search, setSearch] = useState("");

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(!open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [open, onOpenChange]);

  const handleSelect = useCallback((callback: () => void) => {
    onOpenChange(false);
    callback();
  }, [onOpenChange]);

  const handleSignOut = async () => {
    await signOut();
    toast.success("Logout realizado com sucesso");
    navigate("/");
  };

  const pages = [
    { icon: Home, label: "Início", path: "/" },
    { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard" },
    { icon: Wrench, label: "Ferramentas", path: "/ferramentas" },
    { icon: MessageSquare, label: "Conversas", path: "/conversations" },
    { icon: BookOpen, label: "Guias", path: "/guias" },
    { icon: CreditCard, label: "Planos", path: "/planos" },
  ];

  const tools = [
    { icon: FileSpreadsheet, label: "Planilhas", path: "/ferramentas?category=planilhas" },
    { icon: Bot, label: "Agentes GPT", path: "/ferramentas?category=agentes" },
    { icon: FileText, label: "Prompts", path: "/ferramentas?category=prompts" },
    { icon: BookMarked, label: "E-books", path: "/ferramentas?category=ebooks" },
  ];

  const actions = [
    { icon: theme === "dark" ? Sun : Moon, label: "Alternar Tema", action: () => setTheme(theme === "dark" ? "light" : "dark") },
    { icon: HelpCircle, label: "Atalhos de Teclado", action: onShowHelp },
    { icon: Settings, label: "Configurações", action: () => navigate("/dashboard") },
    ...(user ? [{ icon: LogOut, label: "Sair", action: handleSignOut }] : []),
  ];

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput 
        placeholder="Digite um comando ou pesquise..." 
        value={search}
        onValueChange={setSearch}
      />
      <CommandList>
        <CommandEmpty>Nenhum resultado encontrado.</CommandEmpty>
        
        <CommandGroup heading="Navegação">
          {pages.map((page) => (
            <CommandItem
              key={page.path}
              onSelect={() => handleSelect(() => navigate(page.path))}
            >
              <page.icon className="mr-2 h-4 w-4" />
              <span>{page.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Ferramentas">
          {tools.map((tool) => (
            <CommandItem
              key={tool.path}
              onSelect={() => handleSelect(() => navigate(tool.path))}
            >
              <tool.icon className="mr-2 h-4 w-4" />
              <span>{tool.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Ações">
          {actions.map((action) => (
            <CommandItem
              key={action.label}
              onSelect={() => handleSelect(action.action)}
            >
              <action.icon className="mr-2 h-4 w-4" />
              <span>{action.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
